'use client';

import React from 'react';
import { Box, Flex, Text } from '@skin-seoul/design-system';
import {
  formatCurrency,
  useCartService,
  useGetShippingPolicy,
  useRegionCurrency,
  type Country,
} from '@skin-seoul/service';
import { CheckIcon } from './components/CheckIcon';
import { ConfettiAnimation } from './components/ConfettiAnimation';
import { thresholdsPerCountry, MilestoneKey, type Threshold } from './constants';
import { buildCampaignMilestones, computeCampaignProgressAnchored } from './utils';
import { BMSMGradient } from './components/BMSMGradient';
import { Condition } from '@skin-seoul/utils';

export type BMSMCampaignState =
  | 'Empty cart'
  | 'Less than free shipping'
  | 'Free shipping available'
  | 'Less than 10% OFF'
  | '10% OFF available'
  | 'Less than 15% OFF'
  | '15% OFF available';

interface BMSMCampaignBannerProps {
  /** Override automatic state calculation with manual state */
  state?: BMSMCampaignState;
  className?: string;
  /** Banner height in px */
  height?: number;
}

const stateToStep: Record<BMSMCampaignState, number> = {
  'Empty cart': 0,
  'Less than free shipping': 1,
  'Free shipping available': 2,
  'Less than 10% OFF': 3,
  '10% OFF available': 4,
  'Less than 15% OFF': 5,
  '15% OFF available': 6,
};

export function BMSMCampaignBanner({ state: manualState, className = '', height = 130 }: BMSMCampaignBannerProps) {
  const { country, currency } = useRegionCurrency();
  const { useGetCartSuspenseQuery } = useCartService();
  const { shippingPolicy } = useGetShippingPolicy();

  // Get cart data
  const { data: cartData } = useGetCartSuspenseQuery({ currency });

  // Get thresholds for current country
  const thresholds = thresholdsPerCountry[country as Country];
  const cartTotal = cartData?.cart.totals.total ?? 0;
  const cartTotalPrice = cartData?.cart.totals.totalPrice ?? 0;
  const cartTotalDiscount = cartData?.cart.totals.totalDiscount ?? 0;
  // Fallback to per-country default when shipping policy hasn't loaded yet
  const freeShippingThreshold = shippingPolicy?.freeShippingThreshold ?? thresholds.freeShipping;
  const shippingFee = shippingPolicy?.shippingFee ?? 0;
  const _isFreeShipping = cartData?.cart.shippingRates?.length === 0 || cartData?.cart.shippingRates?.[0]?.price === 0;

  // Calculate campaign state based on cart subtotal (items total)
  const calculateState = (): number => {
    if (manualState) {
      return stateToStep[manualState];
    }

    const { tenPercentOff, fifteenPercentOff } = thresholds;

    if (cartTotalPrice === 0) {
      return 0;
    }

    if (cartTotalPrice < freeShippingThreshold) {
      return 1;
    }

    if (cartTotalPrice < tenPercentOff) {
      return 2; // Free shipping available
    }

    if (cartTotalPrice < (fifteenPercentOff as number)) {
      return 4; // 10% OFF available
    }

    return 6; // 15% OFF available
  };

  const state = calculateState();

  // Fixed layers: labels above, track (icons+bar) below
  const LABEL_AREA_HEIGHT_PX = 52;
  const ICON_SIZE_PX = 24;
  const BAR_HEIGHT_PX = 4;
  const CHECK_TEXT_GAP_PX = 12;

  const resolvedThresholds: Threshold = {
    freeShipping: freeShippingThreshold,
    tenPercentOff: thresholds.tenPercentOff,
    fifteenPercentOff: thresholds.fifteenPercentOff,
  };

  const milestones = buildCampaignMilestones(resolvedThresholds, cartTotalPrice, currency);

  const showFreeShippingConfetti = milestones[0]?.achieved ?? false;
  const showDiscountConfetti = milestones.find((m) => m.key === MilestoneKey.TenPercentOff)?.achieved ?? false;
  const progress = computeCampaignProgressAnchored(resolvedThresholds, cartTotalPrice);
  const getSavingAmount = (): number => {
    if (cartTotalPrice <= cartTotal) {
      return shippingFee;
    }
    return shippingFee + cartTotalDiscount;
  };

  return (
    <Flex
      position="relative"
      width="100%"
      direction="column"
      bg="generic.100"
      overflow="hidden"
      alignItems="center"
      justifyContent="center"
      py="16px"
      minH={`${height}px`}
      height={`${height}px`}
      gap={2}
      borderRadius="16px"
      className={className}
      data-name={`State=${state}`}
    >
      {/* Overlay inside track area will handle positioning */}

      <BMSMGradient />

      <Flex
        zIndex={1}
        position="relative"
        width="100%"
        padding="0 16px"
        height={`${LABEL_AREA_HEIGHT_PX + ICON_SIZE_PX}px`}
        alignItems="center"
        justifyContent="center"
      >
        <Box position="absolute" left="0" right="0" bottom="0" height={`${ICON_SIZE_PX}px`}>
          <Box
            position="absolute"
            left="16px"
            right="16px"
            top={`${(ICON_SIZE_PX - BAR_HEIGHT_PX) / 2}px`}
            height={`${BAR_HEIGHT_PX}px`}
            bg="neutral.600"
            borderRadius="2px"
          >
            <Box
              width={`${progress}%`}
              height="100%"
              bg="success.600"
              borderRadius="2px"
              transition="width 0.3s ease"
            />
          </Box>
          <Box
            position="absolute"
            left="16px"
            right="16px"
            top={`-${LABEL_AREA_HEIGHT_PX}px`}
            height={`${LABEL_AREA_HEIGHT_PX + ICON_SIZE_PX}px`}
          >
            {milestones.map((m) => {
              const clamped = Math.max(0, Math.min(1, m.anchorPercent));

              const left = (() => {
                if (clamped === 0) {
                  return '0%';
                }
                if (clamped === 1) {
                  return '100%';
                }
                return `${clamped * 100}%`;
              })();

              const translate = (() => {
                if (clamped === 0) {
                  return 'translateX(0)';
                }
                if (clamped === 1) {
                  return 'translateX(-100%)';
                }
                return 'translateX(-50%)';
              })();
              return (
                <React.Fragment key={m.key}>
                  <Flex
                    position="absolute"
                    left={left}
                    transform={translate}
                    bottom={`${ICON_SIZE_PX + CHECK_TEXT_GAP_PX}px`}
                    direction="column"
                    gap="2px"
                    align="center"
                  >
                    <Text
                      textStyle="label.small"
                      whiteSpace="nowrap"
                      fontWeight="medium"
                      color={m.achieved ? 'primary.200' : 'neutral.400'}
                    >
                      {m.statusText}
                    </Text>
                    <Text
                      textStyle="label.large"
                      whiteSpace="nowrap"
                      fontWeight="bold"
                      color="generic.0"
                      letterSpacing="-0.64px"
                    >
                      {m.title}
                    </Text>
                  </Flex>
                  <Box position="absolute" left={left} transform={translate} bottom="0" width="24px" height="24px">
                    <CheckIcon achieved={m.achieved} />
                  </Box>
                </React.Fragment>
              );
            })}
          </Box>
        </Box>
      </Flex>
      <ConfettiAnimation
        showFreeShippingConfetti={showFreeShippingConfetti}
        showDiscountConfetti={showDiscountConfetti}
      />
      <Flex align="center" gap="2px" width="100%" zIndex={1} justify="center">
        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="2" viewBox="0 0 22 2" fill="none">
          <path d="M21 1H1" stroke="url(#paint0_linear_2188_5477)" strokeLinecap="round" />
          <defs>
            <linearGradient id="paint0_linear_2188_5477" x1="21" y1="0" x2="1" y2="0" gradientUnits="userSpaceOnUse">
              <stop stopColor="white" />
              <stop offset="1" stopOpacity="0" />
            </linearGradient>
          </defs>
        </svg>

        <Condition
          expression={state < 2}
          then={
            <Text textStyle="label.xsmall" whiteSpace="nowrap" fontWeight="medium" color="generic.0">
              New here? Free shipping with no min spend!
            </Text>
          }
          else={
            <>
              <Text textStyle="label.xsmall" fontWeight="semibold" color="generic.0" whiteSpace="pre" mr="2px">
                {formatCurrency(currency, getSavingAmount())} saved
              </Text>
              <Text textStyle="label.xsmall" whiteSpace="nowrap" fontWeight="medium" color="generic.0">
                so far! — let’s see how far it can go!
              </Text>
            </>
          }
        />

        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="2" viewBox="0 0 22 2" fill="none">
          <path d="M1 1H21" stroke="url(#paint0_linear_2188_5479)" strokeLinecap="round" />
          <defs>
            <linearGradient id="paint0_linear_2188_5479" x1="1" y1="2" x2="21" y2="2" gradientUnits="userSpaceOnUse">
              <stop stopColor="white" />
              <stop offset="1" stopOpacity="0" />
            </linearGradient>
          </defs>
        </svg>
      </Flex>
    </Flex>
  );
}
